const insights = [
  {
    icon: 'ri-eye-line',
    title: 'Выявление слепых зон',
    desc: 'Во время игры становятся видны скрытые паттерны: кто берет лидерство, кто саботирует, как принимаются решения.',
  },
  {
    icon: 'ri-flashlight-line',
    title: 'Безопасная среда',
    desc: 'Игровой формат позволяет обсуждать сложные вопросы без обвинений. «Это было в игре» — легче принять обратную связь.',
  },
  {
    icon: 'ri-brain-line',
    title: 'Инсайты через опыт',
    desc: 'Люди не просто слушают теорию — они сами проходят ситуацию и делают выводы. Это запоминается на годы.',
  },
  {
    icon: 'ri-team-line',
    title: 'Единый опыт',
    desc: 'Вся команда проходит через одну ситуацию. Это создает общий язык, общие метафоры и общее понимание проблем.',
  },
];

const skills = [
  'Командное принятие решений',
  'Коммуникация под давлением',
  'Распределение ролей',
  'Управление конфликтами',
  'Стратегическое мышление',
  'Адаптивность к изменениям',
  'Креативное решение проблем',
  'Переговоры и влияние',
];

export default function BusinessGames() {
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-3xl md:text-5xl font-bold text-gray-900 text-center mb-6">
          Бизнес-игры как инструмент командообразования
        </h2>
        <p className="text-lg text-gray-600 text-center mb-16 max-w-3xl mx-auto leading-relaxed">
          Бизнес-игры — это не развлечение. Это инструмент, который работает сильнее лекций и тренингов, потому что создает <strong>живой опыт</strong>, который команда переживает вместе.
        </p>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Почему бизнес-игры работают сильнее развлечений
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Развлекательные игры (квесты, пейнтбол) дают эмоции, но не дают инсайтов о работе команды. Бизнес-игры моделируют <strong>реальные бизнес-ситуации</strong>: конкуренция, ограниченные ресурсы, неопределенность, необходимость быстро принимать решения. В этом процессе команда видит себя со стороны и получает мощные инсайты.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Какие навыки развиваются</h3>
              <div className="grid grid-cols-2 gap-3">
                {skills.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-4 py-2.5">
                    <i className="ri-check-line text-teal-600"></i>
                    <span className="text-sm text-gray-700">{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Что происходит во время игры</h3>
              <p className="text-gray-600 leading-relaxed">
                Команда получает задание, распределяет роли, разрабатывает стратегию и действует в условиях неопределенности. Происходят ошибки, конфликты, переговоры, компромиссы — <strong>все то же самое, что и в реальной работе</strong>, но в сжатом времени и безопасной среде. После игры проводится глубокий разбор.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Какие инсайты получает команда</h3>
              <div className="space-y-4">
                {insights.map((ins, i) => (
                  <div key={i} className="flex items-start gap-3 bg-gray-50 rounded-xl p-4">
                    <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${ins.icon} text-lg text-white`}></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{ins.title}</h4>
                      <p className="text-gray-600 text-sm mt-1">{ins.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Фотографии игровых форматов */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl overflow-hidden h-72">
            <img
              src="https://readdy.ai/api/search-image?query=Professional%20corporate%20team%20working%20together%20around%20a%20table%20in%20a%20modern%20training%20room%2C%20business%20game%20session%20with%20strategy%20maps%20and%20colorful%20sticky%20notes%20on%20whiteboard%2C%20engaged%20employees%20collaborating%20in%20small%20groups%2C%20bright%20natural%20lighting%2C%20clean%20minimalist%20corporate%20interior%2C%20editorial%20photography%20style%20with%20warm%20and%20professional%20atmosphere&width=800&height=600&seq=team-building-game-01&orientation=landscape"
              alt="Бизнес-игра для команды — участники работают в группах"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="rounded-2xl overflow-hidden h-72">
            <img
              src="https://readdy.ai/api/search-image?query=Corporate%20training%20workshop%20with%20diverse%20team%20engaged%20in%20strategic%20business%20simulation%2C%20participants%20discussing%20and%20presenting%20ideas%20near%20flipchart%2C%20professional%20conference%20room%20setting%20with%20modern%20furniture%2C%20natural%20window%20light%2C%20warm%20neutral%20color%20palette%2C%20clean%20and%20collaborative%20vibe%2C%20editorial%20commercial%20photography&width=800&height=600&seq=team-building-game-02&orientation=landscape"
              alt="Командное обучение — стратегическая игра на тренинге"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </div>
    </section>
  );
}